import React, { useState } from "react"

import BoatPreviewLink from "src/components/BoatPreviewLink"
import Button from "src/components/Button"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import { getWorkOrderIds, getWorkOrders } from "src/api/Service"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useTracker } from "src/hooks/use_tracker"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import CreateWorkOrderModal from "./CreateWorkOrderModal"
import FilterChips from "./FilterChips"
import WorkOrderJobProgress from "./WorkOrderJobProgress"
import WorkOrderStatusBadge from "./WorkOrderStatusBadge"

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "work_complete", label: "Work Complete" },
  { value: "closed", label: "Closed" },
]

const SCHEDULED_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "unscheduled", label: "Unscheduled" },
]

const WorkOrdersTable = () => {
  const marinaSlug = getCurrentMarinaSlug()
  const tracker = useTracker()
  const table = useDatatable({
    queryKey: ["workOrders", marinaSlug],
    fetchFn: getWorkOrders,
    defaultFilters: { search: "", status: [], scheduled: [] },
    fetchAllIds: (params) => getWorkOrderIds(params).then((r) => r.ids),
  })
  const [searchTerm, setSearchTerm] = useState(table.filters.search || "")
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const COLUMNS = [
    {
      key: "id",
      header: "ID",
      sortable: false,
      cardRole: "badge",
      width: "150px",
      render: (row) => (
        <div title={row.displaySequentialId}>{row.displaySequentialId}</div>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      sortable: true,
      cardRole: "subtitle",
      render: (row) => (
        <div className="truncate" title={row.contact.name || row.contact.email}>
          {row.contact.name || row.contact.email}
        </div>
      ),
    },
    {
      key: "boat",
      header: "Boat",
      sortable: true,
      cardRole: "title",
      render: (row) => <BoatPreviewLink boat={row.contactBoat} />,
    },
    {
      key: "status",
      header: "Status",
      sortable: false,
      cardRole: "detail",
      render: (row) => (
        <WorkOrderStatusBadge
          status={row.status}
          statusLabel={row.statusLabel}
        />
      ),
    },
    {
      key: "jobProgress",
      header: "Job Progress",
      sortable: false,
      cardRole: "detail",
      width: "180px",
      render: (row) => <WorkOrderJobProgress jobProgress={row.jobProgress} />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cardRole: "detail",
      render: (row) =>
        row.createdAtFormatted ? (
          <div>{row.createdAtFormatted}</div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "scheduledAt",
      header: "Scheduled",
      sortable: true,
      cardRole: "detail",
      render: (row) =>
        row.scheduledAtFormatted ? (
          <div>{row.scheduledAtFormatted}</div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "closedAt",
      header: "Closed",
      sortable: true,
      cardRole: "detail",
      render: (row) =>
        row.closedAtFormatted ? (
          <div>{row.closedAtFormatted}</div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "timeToClose",
      header: "Time to Close",
      sortable: true,
      width: "150px",
      cardRole: "detail",
      render: (row) =>
        row.timeToCloseLabel ? (
          <div>{row.timeToCloseLabel}</div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
  ]

  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
    if (value) {
      tracker.trackEvent("work_orders:list:searched", { length: value.length })
    }
  })

  const handleClearSearch = () => {
    setSearchTerm("")
    debouncedSetSearch("")
    table.setFilter("search", "")
  }

  const handleSearch = (event) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSetSearch(value.trim())
  }

  const handleStatusChange = (values) => {
    table.setFilter("status", values)
    tracker.trackEvent("work_orders:list:status_filter_changed", {
      statuses: values,
    })
  }

  const handleScheduledChange = (values) => {
    table.setFilter("scheduled", values)
    tracker.trackEvent("work_orders:list:scheduled_filter_changed", {
      scheduled: values,
    })
  }

  const handleClearAll = () => {
    setSearchTerm("")
    table.clearAllFilters()
  }

  if (table.isError) {
    return (
      <div className="text-muted p-5 text-center">
        <h3 className="mb-5 text-lg font-semibold">
          Error loading work orders
        </h3>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="w-full xl:w-1/2">
          <Form.IconTextField
            icon={<i className="icon icon-search-mdi text-xl text-gray-400" />}
            id="work-order-search"
            isClearable
            onChange={handleSearch}
            onClearSelection={handleClearSearch}
            position="left"
            value={searchTerm}
            placeholder="Search by work order id, customer name or boat name"
          />
        </div>
        <Select
          multiple
          value={table.filters.status}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger
            variant="button"
            className="w-full sm:w-auto"
            aria-label="Status"
          >
            Status
          </SelectTrigger>
          <SelectContent className="max-h-none">
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          multiple
          value={table.filters.scheduled}
          onValueChange={handleScheduledChange}
        >
          <SelectTrigger
            variant="button"
            className="w-full sm:w-auto"
            aria-label="Scheduled"
          >
            Scheduled
          </SelectTrigger>
          <SelectContent className="max-h-none">
            {SCHEDULED_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="sm:ml-auto">
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            Add new
          </Button>
        </div>
      </div>
      <FilterChips
        table={table}
        statusOptions={STATUS_OPTIONS}
        scheduledOptions={SCHEDULED_OPTIONS}
        onClearSearch={handleClearSearch}
        onClearAll={handleClearAll}
      />
      <DataTableV2
        name="work-orders-table"
        columns={COLUMNS}
        data={table.records}
        maxHeight="none"
        rowConfig={{ getViewUrl: (row) => row.path }}
        emptyState={
          <span className="text-gray-500">
            {table.activeFilters.length > 0
              ? "No work orders match your search"
              : "No work orders to show"}
          </span>
        }
        {...table.tableProps}
      />
      <CreateWorkOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  )
}

export default WorkOrdersTable
